import React, { useCallback, useEffect, useState } from "react";
import api from "../api/client";

interface Entry {
  id: string;
  date: string; // YYYY-MM-DD
  title: string;
  entry_type: string;
}

interface DayInfo {
  session: { id: string; date: string; topic?: string; lesson_number?: number; notes?: string } | null;
  attendance?: { student: string; status: string; notes?: string }[];
  report?: {
    overall_notes?: string;
    observations?: Record<string, string[]> | null;
    ratings: { student: string; rating: string; notes?: string }[];
  } | null;
}

const OBSERVATION_LABELS: Record<string, string> = {
  behavior: "Behaviour / class rules",
  posture: "Posture in class",
  class_responses: "Class responses",
  next_steps: "Next steps",
};

export const TYPE_STYLES: Record<string, { bg: string; text: string; dot: string; label: string }> = {
  lesson: { bg: "#eff6ff", text: "#1d4ed8", dot: "#3b82f6", label: "Lesson" },
  bb: { bg: "#ecfeff", text: "#0e7490", dot: "#06b6d4", label: "BB" },
  special: { bg: "#fffbeb", text: "#b45309", dot: "#f59e0b", label: "Special" },
  service: { bg: "#f0fdf4", text: "#15803d", dot: "#22c55e", label: "Service" },
  passover: { bg: "#faf5ff", text: "#7e22ce", dot: "#a855f7", label: "Passover" },
  no_class: { bg: "#fef2f2", text: "#b91c1c", dot: "#ef4444", label: "No class" },
  duty_translator: { bg: "#eef2ff", text: "#4338ca", dot: "#6366f1", label: "Translator" },
  duty_pre_review: { bg: "#f0fdfa", text: "#0f766e", dot: "#14b8a6", label: "Pre review" },
  duty_post_review: { bg: "#f8fafc", text: "#475569", dot: "#94a3b8", label: "Post review" },
};

export const isDuty = (t: string) => t.startsWith("duty_");

const RATING_COLORS: Record<string, string> = {
  green: "#16a34a",
  orange: "#e67e22",
  yellow: "#eab308",
  red: "#dc2626",
};

const ATT_STYLES: Record<string, { label: string; bg: string; text: string }> = {
  IP: { label: "Present", bg: "#f0fdf4", text: "#15803d" },
  X: { label: "Absent", bg: "#fef2f2", text: "#b91c1c" },
  CU: { label: "Late", bg: "#fffbeb", text: "#b45309" },
};

const pad = (n: number) => String(n).padStart(2, "0");
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromIso = (s: string) => new Date(Number(s.slice(0, 4)), Number(s.slice(5, 7)) - 1, Number(s.slice(8, 10)));
const mondayOf = (d: Date) => {
  const x = new Date(d);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
};

interface EditorState {
  date: string;
  entry: Entry | null;
  isDirection: boolean;
}

export default function ClassCalendar({ classId }: { classId: string }) {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [month, setMonth] = useState<Date>(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), 1);
  });
  const [anim, setAnim] = useState<"left" | "right" | null>(null);
  const [openDay, setOpenDay] = useState<string | null>(null);
  const [dayInfo, setDayInfo] = useState<DayInfo | null>(null);
  const [dayLoading, setDayLoading] = useState(false);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [title, setTitle] = useState("");
  const [entryType, setEntryType] = useState("lesson");
  const [shiftLater, setShiftLater] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get(`/calendar/${classId}`);
    setEntries(r.data);
  }, [classId]);

  useEffect(() => {
    load();
  }, [load]);

  const dayEntries: Record<string, Entry[]> = {};
  const directions: Record<string, Entry> = {};
  for (const e of entries) {
    if (e.entry_type === "direction") directions[e.date] = e;
    else (dayEntries[e.date] = dayEntries[e.date] || []).push(e);
  }

  const today = new Date();
  const sortedDates = entries.map((e) => e.date).sort();
  const firstMonday = sortedDates.length > 0 ? mondayOf(fromIso(sortedDates[0])) : mondayOf(today);
  const weekNo = (monday: Date) => Math.round((monday.getTime() - firstMonday.getTime()) / (7 * 24 * 3600 * 1000));

  const nav = (dir: -1 | 1) => {
    setAnim(dir === 1 ? "left" : "right");
    setMonth(new Date(month.getFullYear(), month.getMonth() + dir, 1));
  };

  const openDayPanel = async (dIso: string) => {
    setOpenDay(dIso);
    setDayInfo(null);
    setDayLoading(true);
    try {
      const r = await api.get(`/calendar/${classId}/day/${dIso}`);
      setDayInfo(r.data);
    } catch {
      setDayInfo({ session: null });
    } finally {
      setDayLoading(false);
    }
  };

  const openEditor = (st: EditorState) => {
    setEditor(st);
    setTitle(st.entry?.title || "");
    setEntryType(st.isDirection ? "direction" : st.entry?.entry_type || "lesson");
    setShiftLater(false);
  };

  const save = async () => {
    if (!editor || !title.trim()) return;
    setSaving(true);
    try {
      if (editor.entry) {
        await api.patch(`/calendar/${classId}/${editor.entry.id}`, { title: title.trim(), entry_type: entryType });
      } else if (shiftLater && !editor.isDirection) {
        await api.post(`/calendar/${classId}/insert-shift`, { date: editor.date, title: title.trim(), entry_type: entryType });
      } else {
        await api.post(`/calendar/${classId}`, { date: editor.date, title: title.trim(), entry_type: entryType });
      }
      await load();
      setEditor(null);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!editor?.entry) return;
    setSaving(true);
    try {
      await api.delete(`/calendar/${classId}/${editor.entry.id}`);
      await load();
      setEditor(null);
    } finally {
      setSaving(false);
    }
  };

  // Weeks (Mon-based) overlapping the visible month
  const monthStart = new Date(month.getFullYear(), month.getMonth(), 1);
  const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  const weeks: Date[] = [];
  for (let m = mondayOf(monthStart); m <= monthEnd; m = new Date(m.getFullYear(), m.getMonth(), m.getDate() + 7)) {
    weeks.push(new Date(m));
  }

  const monthTitle = month.toLocaleDateString("en-GB", { month: "long", year: "numeric" });

  return (
    <div>
      <style>{`
        @keyframes cal-slide-left { from { opacity: 0.35; transform: translateX(28px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes cal-slide-right { from { opacity: 0.35; transform: translateX(-28px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes cal-drawer-in { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .cal-slide-left { animation: cal-slide-left 0.22s ease-out; }
        .cal-slide-right { animation: cal-slide-right 0.22s ease-out; }
        .cal-nav-btn { border: 1px solid var(--border-light); background: var(--card, #fff); border-radius: 8px; width: 34px; height: 34px; font-size: 16px; cursor: pointer; line-height: 1; }
        .cal-nav-btn:hover { background: var(--bg); }
        .cal-day { transition: box-shadow 0.12s ease, transform 0.12s ease; }
        .cal-day:hover { box-shadow: 0 2px 10px rgba(0,0,0,0.09); transform: translateY(-1px); }
      `}</style>

      {/* Header: navigation + legend */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <button className="cal-nav-btn" onClick={() => nav(-1)} aria-label="Previous month">‹</button>
        <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, margin: 0, minWidth: 170, textAlign: "center" }}>{monthTitle}</h3>
        <button className="cal-nav-btn" onClick={() => nav(1)} aria-label="Next month">›</button>
        <button
          className="cal-nav-btn"
          style={{ width: "auto", padding: "0 12px", fontSize: 12, fontWeight: 600 }}
          onClick={() => {
            setAnim(null);
            setMonth(new Date(today.getFullYear(), today.getMonth(), 1));
          }}
        >
          Today
        </button>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginLeft: "auto", fontSize: 11 }}>
          {Object.entries(TYPE_STYLES).map(([k, s]) => (
            <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: s.bg, color: s.text, borderRadius: 999, padding: "3px 9px", fontWeight: 600 }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: s.dot }} />
              {s.label}
            </span>
          ))}
        </div>
      </div>

      {/* Month grid */}
      <div style={{ overflowX: "auto" }}>
        <div key={iso(month)} className={anim === "left" ? "cal-slide-left" : anim === "right" ? "cal-slide-right" : undefined} style={{ minWidth: 960 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr) 210px", gap: 6, marginBottom: 6 }}>
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun", "Direction"].map((h) => (
              <div key={h} style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.5, textAlign: "center", padding: "2px 0" }}>
                {h}
              </div>
            ))}
          </div>
          {weeks.map((monday) => {
            const mondayIso = iso(monday);
            const dir = directions[mondayIso];
            return (
              <div key={mondayIso} style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr) 210px", gap: 6, marginBottom: 6 }}>
                {[0, 1, 2, 3, 4, 5, 6].map((i) => {
                  const d = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i);
                  const inMonth = d.getMonth() === month.getMonth();
                  const dIso = iso(d);
                  const isToday = dIso === iso(today);
                  const isWeekend = i >= 5;
                  if (!inMonth) {
                    return <div key={i} style={{ borderRadius: 10, background: "transparent", minHeight: 86 }} />;
                  }
                  return (
                    <div
                      key={i}
                      className="cal-day"
                      onClick={() => openDayPanel(dIso)}
                      style={{
                        borderRadius: 10,
                        border: isToday ? "2px solid var(--accent, #b45309)" : "1px solid var(--border-light)",
                        background: isWeekend ? "var(--bg)" : "var(--card, #fff)",
                        minHeight: 86,
                        padding: 6,
                        cursor: "pointer",
                      }}
                    >
                      <div style={{ fontSize: 11, fontWeight: isToday ? 800 : 600, color: isToday ? "var(--accent, #b45309)" : "var(--text-muted)", marginBottom: 3 }}>
                        {d.getDate()}
                      </div>
                      {(dayEntries[dIso] || [])
                        .slice()
                        .sort((a, b) => (isDuty(a.entry_type) ? 1 : 0) - (isDuty(b.entry_type) ? 1 : 0))
                        .map((e) => {
                          const s = TYPE_STYLES[e.entry_type] || TYPE_STYLES.lesson;
                          const duty = isDuty(e.entry_type);
                          return (
                            <div
                              key={e.id}
                              title={duty ? `${s.label}: ${e.title}` : e.title}
                              style={{ display: "flex", alignItems: "flex-start", gap: 4, background: s.bg, color: s.text, borderRadius: 6, padding: duty ? "2px 5px" : "3px 5px", marginTop: 2, fontWeight: 600, fontSize: duty ? 10.5 : 11, lineHeight: 1.3 }}
                            >
                              <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.dot, marginTop: 3, flexShrink: 0 }} />
                              <span>{duty ? <><span style={{ opacity: 0.75 }}>{s.label}:</span> {e.title}</> : e.title}</span>
                            </div>
                          );
                        })}
                    </div>
                  );
                })}
                <div
                  onClick={() => openEditor({ date: mondayIso, entry: dir || null, isDirection: true })}
                  style={{ borderRadius: 10, border: "1px dashed var(--border-light)", background: dir ? "#fafaf9" : "transparent", minHeight: 86, padding: 8, fontSize: 11.5, lineHeight: 1.45, cursor: "pointer" }}
                >
                  {sortedDates.length > 0 && weekNo(monday) >= 0 && (
                    <span style={{ display: "inline-block", background: "var(--bg)", border: "1px solid var(--border-light)", borderRadius: 999, padding: "1px 8px", fontSize: 10, fontWeight: 700, color: "var(--text-muted)", marginBottom: 4 }}>
                      Week {weekNo(monday)}
                    </span>
                  )}
                  <div style={{ color: dir ? "var(--text)" : "var(--text-muted)", fontStyle: dir ? "normal" : "italic" }}>
                    {dir?.title || "Set direction…"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Day drawer */}
      {openDay && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 90 }} onClick={() => setOpenDay(null)}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: 400, maxWidth: "92vw", background: "var(--card, #fff)", boxShadow: "-6px 0 24px rgba(0,0,0,0.15)", padding: 20, overflowY: "auto", animation: "cal-drawer-in 0.2s ease-out" }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 18, margin: 0 }}>
                {fromIso(openDay).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
              </h3>
              <button onClick={() => setOpenDay(null)} style={{ border: "none", background: "none", fontSize: 18, cursor: "pointer", color: "var(--text-muted)" }}>✕</button>
            </div>

            {/* Calendar entries for the day */}
            <div style={{ margin: "14px 0 18px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Calendar</div>
              {(dayEntries[openDay] || [])
                .slice()
                .sort((a, b) => (isDuty(a.entry_type) ? 1 : 0) - (isDuty(b.entry_type) ? 1 : 0))
                .map((e) => {
                  const s = TYPE_STYLES[e.entry_type] || TYPE_STYLES.lesson;
                  const duty = isDuty(e.entry_type);
                  return (
                    <div
                      key={e.id}
                      onClick={() => openEditor({ date: openDay, entry: e, isDirection: false })}
                      style={{ display: "flex", alignItems: "flex-start", gap: 6, background: s.bg, color: s.text, borderRadius: 8, padding: "7px 10px", marginBottom: 6, fontWeight: 600, fontSize: 13, cursor: "pointer" }}
                    >
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: s.dot, marginTop: 5, flexShrink: 0 }} />
                      <span>{duty ? <><span style={{ opacity: 0.75 }}>{s.label}:</span> {e.title}</> : e.title}</span>
                      <span style={{ marginLeft: "auto", fontSize: 11, opacity: 0.7 }}>edit</span>
                    </div>
                  );
                })}
              <button
                onClick={() => openEditor({ date: openDay, entry: null, isDirection: false })}
                style={{ fontSize: 12, fontWeight: 600, border: "1px dashed var(--border-light)", background: "none", borderRadius: 8, padding: "6px 12px", cursor: "pointer", color: "var(--text-muted)", width: "100%" }}
              >
                + Add entry
              </button>
            </div>

            {/* Lesson report / student status */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Lesson report</div>
              {dayLoading && <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Loading…</div>}
              {!dayLoading && dayInfo && !dayInfo.session && (
                <div style={{ fontSize: 13, color: "var(--text-muted)" }}>No session recorded for this day.</div>
              )}
              {!dayLoading && dayInfo?.session && (
                <>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>
                    {dayInfo.session.lesson_number ? `L${dayInfo.session.lesson_number} — ` : ""}{dayInfo.session.topic || "Session"}
                  </div>
                  {dayInfo.report?.overall_notes && (
                    <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: 10, lineHeight: 1.5 }}>{dayInfo.report.overall_notes}</div>
                  )}
                  {dayInfo.report && !dayInfo.report.overall_notes &&
                    Object.entries(OBSERVATION_LABELS).map(([key, label]) => {
                      const items = dayInfo.report?.observations?.[key] || [];
                      return (
                        <div key={key} style={{ margin: "8px 0 10px" }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", marginBottom: 3 }}>{label.toUpperCase()}</div>
                          {items.length === 0 && (
                            <div style={{ fontSize: 12.5, color: "var(--text-muted)", fontStyle: "italic" }}>None noted</div>
                          )}
                          {items.map((it, i) => (
                            <div key={i} style={{ fontSize: 12.5, lineHeight: 1.5, paddingLeft: 12, position: "relative" }}>
                              <span style={{ position: "absolute", left: 0 }}>•</span>
                              {it}
                            </div>
                          ))}
                        </div>
                      );
                    })}

                  {/* Attendance summary */}
                  {dayInfo.attendance && dayInfo.attendance.length > 0 && (
                    <div style={{ margin: "10px 0 14px" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", marginBottom: 5 }}>
                        ATTENDANCE · {dayInfo.attendance.filter((a) => a.status === "IP").length}/{dayInfo.attendance.length} present
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                        {dayInfo.attendance.map((a) => {
                          const s = ATT_STYLES[a.status] || ATT_STYLES.IP;
                          return (
                            <span key={a.student} title={`${s.label}${a.notes ? ` — ${a.notes}` : ""}`} style={{ background: s.bg, color: s.text, borderRadius: 999, padding: "3px 10px", fontSize: 12, fontWeight: 600 }}>
                              {a.student}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Per-student ratings — only when the report actually graded students
                      (Aug CT's report format has no rating sections, so nothing shows there) */}
                  {dayInfo.report && dayInfo.report.ratings.length > 0 && (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", marginBottom: 5 }}>STUDENT STATUS</div>
                      {dayInfo.report.ratings.map((r) => (
                        <div key={r.student} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--border-light)" }}>
                          <span style={{ width: 10, height: 10, borderRadius: "50%", background: RATING_COLORS[r.rating] || "#9ca3af", marginTop: 4, flexShrink: 0 }} />
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700 }}>{r.student}</div>
                            {r.notes && <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.45 }}>{r.notes}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Entry editor modal */}
      {editor && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 110 }}
          onClick={() => setEditor(null)}
        >
          <div style={{ background: "var(--card, #fff)", borderRadius: 14, padding: 22, width: 380, maxWidth: "90vw", boxShadow: "0 12px 40px rgba(0,0,0,0.18)" }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 16, margin: "0 0 4px" }}>
              {editor.isDirection ? "Week direction" : editor.entry ? "Edit entry" : "New entry"}
            </h3>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10 }}>
              {editor.isDirection ? `Week of ${editor.date}` : editor.date}
            </div>
            <textarea
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              rows={3}
              placeholder={editor.isDirection ? "Focus for this week…" : isDuty(entryType) ? "Name of the person on duty" : "e.g. Introductory L1 — 2 Kinds of Spirit"}
              style={{ width: "100%", boxSizing: "border-box", fontSize: 13.5, padding: 10, marginBottom: 10, borderRadius: 10, border: "1px solid var(--border-light)", fontFamily: "'DM Sans', -apple-system, sans-serif", lineHeight: 1.5, resize: "vertical" }}
            />
            {!editor.isDirection && (
              <select value={entryType} onChange={(e) => setEntryType(e.target.value)} style={{ width: "100%", fontSize: 13.5, padding: "8px 10px", marginBottom: 14, borderRadius: 10, border: "1px solid var(--border-light)", fontFamily: "'DM Sans', -apple-system, sans-serif", background: "var(--card, #fff)" }}>
                {Object.entries(TYPE_STYLES).map(([k, s]) => (
                  <option key={k} value={k}>
                    {s.label}
                  </option>
                ))}
              </select>
            )}
            {!editor.entry && !editor.isDirection && !isDuty(entryType) && (
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12.5, marginBottom: 12, cursor: "pointer", lineHeight: 1.4 }}>
                <input type="checkbox" checked={shiftLater} onChange={(e) => setShiftLater(e.target.checked)} style={{ marginTop: 2 }} />
                <span>
                  <strong>Insert &amp; shift</strong> — push all later lessons back one class day
                  <span style={{ color: "var(--text-muted)" }}> (services, Passover and no-class days stay put)</span>
                </span>
              </label>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              {editor.entry && (
                <button className="btn btn-danger btn-sm" onClick={remove} disabled={saving} style={{ marginRight: "auto" }}>
                  Delete
                </button>
              )}
              <button className="btn btn-secondary btn-sm" onClick={() => setEditor(null)} disabled={saving}>
                Cancel
              </button>
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || !title.trim()}>
                {editor.entry ? "Save" : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
